import TimeSlider from '@arcgis/core/widgets/TimeSlider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Box } from '@rsos/base-components';
import type {
  TimeSliderComponentProps,
  TimeExtent,
  RootState,
} from './TimeSlider.types';
import { TimeSliderContainer } from './TimeSliderContainer';

// Helper function to maintain compatibility with existing code
const findMapLayerBySublayerId = (map: any, sublayerId: string): any => {
  return map?.mapInstance?.map?.allLayers?.items?.find(
    (layer: any) => layer.id === sublayerId,
  );
};

const TimeSliderComponent = ({ map }: TimeSliderComponentProps) => {
  const timeSliderRef = useRef<TimeSlider | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);

  const activeSupplementalServicesSublayers = useSelector(
    (state: RootState) => state.gisServices.activeSupplementalServicesSublayers,
  );

  const [timeWindow, setTimeWindow] = useState<TimeExtent | null>(null);
  const [fullTimeExtent, setFullTimeExtent] = useState<TimeExtent | null>(null);

  // Filter layers to only include those with time information
  const activeTimeEnabledSublayers = useMemo(() => {
    if (!map?.mapInstance?.map?.allLayers?.items?.length) return [];

    return activeSupplementalServicesSublayers.filter(sublayer => {
      const mapLayer = findMapLayerBySublayerId(map, sublayer.sublayerID);
      return (
        mapLayer &&
        mapLayer.timeInfo &&
        mapLayer.timeInfo.fullTimeExtent &&
        !mapLayer.timeInfo.fullTimeExtent.isEmpty
      );
    });
  }, [activeSupplementalServicesSublayers, map]);

  /**
   * Manages container lifecycle and layer selection
   * - Destroys existing TimeSlider widget and removes container when layers change
   * - Creates new DOM container for map UI integration
   * - Auto-selects appropriate layer when selection becomes invalid
   */
  useEffect(() => {
    if (!map?.mapInstance?.view) return;

    if (timeSliderRef.current && timeSliderRef.current.destroy) {
      timeSliderRef.current.destroy();
    }

    if (mapContainerRef.current && map.mapInstance.view.ui) {
      map.mapInstance.view.ui.remove(mapContainerRef.current);
      mapContainerRef.current = null;
    }

    if (activeTimeEnabledSublayers.length === 0) {
      setSelectedLayerId(null);
      return;
    }

    // Use createElement instead of React component to avoid lifecycle conflicts with ArcGIS map UI
    const container = document.createElement('div');
    mapContainerRef.current = container;
    map.mapInstance.view.ui.add(mapContainerRef.current, 'bottom-right');

    // Auto-select first layer if none selected, or switch if current selection is invalid
    if (!selectedLayerId && activeTimeEnabledSublayers.length > 0) {
      setSelectedLayerId(activeTimeEnabledSublayers[0].sublayerID);
    } else if (
      selectedLayerId &&
      !activeTimeEnabledSublayers.some(
        layer => layer.sublayerID === selectedLayerId,
      )
    ) {
      setSelectedLayerId(activeTimeEnabledSublayers[0].sublayerID);
    }
  }, [map, activeTimeEnabledSublayers, selectedLayerId]);

  // Extract time extent information from the selected map layer
  useEffect(() => {
    if (!map?.mapInstance?.view) return;

    const selectedSublayer = activeTimeEnabledSublayers.find(
      sublayer => sublayer.sublayerID === selectedLayerId,
    );

    if (!selectedLayerId || !selectedSublayer) return;

    map.mapInstance.view.when(() => {
      const mapLayer = findMapLayerBySublayerId(
        map,
        selectedSublayer.sublayerID,
      );

      if (mapLayer?.timeInfo?.fullTimeExtent) {
        const startDate = new Date(mapLayer.timeInfo.fullTimeExtent.start);
        const endDate = new Date(mapLayer.timeInfo.fullTimeExtent.end);
        const newTimeExtent: TimeExtent = { start: startDate, end: endDate };

        setFullTimeExtent(newTimeExtent);
        setTimeWindow(newTimeExtent);
      }
    });
  }, [selectedLayerId, activeTimeEnabledSublayers]);

  // Create ArcGIS TimeSlider widget when time extent data is available
  useEffect(() => {
    if (
      !map?.mapInstance?.view ||
      !fullTimeExtent ||
      !timeWindow ||
      !contentRef.current ||
      activeTimeEnabledSublayers.length === 0
    )
      return;

    try {
      const timeSlider = new TimeSlider({
        container: contentRef.current,
        view: map.mapInstance.view,
        mode: 'time-window',
        timeVisible: true,
        loop: false,
        playRate: 1000,
        fullTimeExtent,
        timeExtent: timeWindow,
        visible: true,
      });

      timeSliderRef.current = timeSlider;
    } catch (error) {
      console.error(error);
    }
  }, [fullTimeExtent, timeWindow, activeTimeEnabledSublayers]);

  useEffect(() => {
    return () => {
      if (timeSliderRef.current?.destroy) {
        timeSliderRef.current.destroy();
        timeSliderRef.current = null;
      }

      if (mapContainerRef.current && map?.mapInstance?.view?.ui) {
        try {
          map.mapInstance.view.ui.remove(mapContainerRef.current);
        } catch (error) {
          console.error(error);
        }
        mapContainerRef.current = null;
      }
    };
  }, [map]);

  if (activeTimeEnabledSublayers.length === 0 || !mapContainerRef.current) {
    return null;
  }

  return createPortal(
    <TimeSliderContainer
      timeEnabledLayers={activeTimeEnabledSublayers}
      isExpanded={isExpanded}
      selectedLayerId={selectedLayerId}
      onLayerChange={setSelectedLayerId}
      onToggle={() => setIsExpanded(prev => !prev)}
    >
      <Box ref={contentRef} />
    </TimeSliderContainer>,
    mapContainerRef.current,
  );
};

export default TimeSliderComponent;
